'use client';

import { PrivyProvider } from '@privy-io/react-auth';

/**
 * App-wide Privy wrapper. Mounted in the root layout so `usePrivy()` is
 * available everywhere — AuthButton, HistoryDrawer, future chat-panel
 * persistence. Configured for the two login methods we care about:
 *
 *   - Google (email-style entry)
 *   - External wallets — Privy auto-routes to the user's installed
 *     wallet, covering Bitcoin (Phantom, Leather, Xverse) and Ethereum
 *     (MetaMask, Rainbow, etc.) under one modal.
 *
 * Theme matches Promit's V1 brandkit — dark surface, amber accent — so
 * the sign-in modal doesn't look bolted on. Embedded wallets are
 * auto-created on Google sign-in so users who don't bring their own
 * still have an on-chain identity for any future wallet-gated features.
 *
 * `NEXT_PUBLIC_PRIVY_APP_ID` is required in production. When unset
 * locally, this renders an inert pass-through wrapper — the app stays
 * usable in anonymous mode and any `usePrivy()` call returns
 * `authenticated: false`, so downstream components branch correctly.
 */
export function PrivyShell({ children }: { children: React.ReactNode }) {
  const appId = process.env.NEXT_PUBLIC_PRIVY_APP_ID;
  if (!appId) {
    return <>{children}</>;
  }
  return (
    <PrivyProvider
      appId={appId}
      config={{
        loginMethods: ['google', 'wallet'],
        appearance: {
          theme: 'dark',
          // Matches `--color-primary` in Promit's tailwind tokens.
          accentColor: '#F7931A',
          showWalletLoginFirst: false,
        },
        embeddedWallets: {
          // Spawn an Ethereum embedded wallet for users who sign in
          // with Google but don't bring their own. Bitcoin / Solana
          // stay bring-your-own — Privy doesn't embed those.
          ethereum: { createOnLogin: 'users-without-wallets' },
        },
      }}
    >
      {children}
    </PrivyProvider>
  );
}
